import { useState, useEffect } from "react";
import { CountryCode } from "utils/countries";
import { PasswordStatus } from "components/PasswordRequirement";

type Account = {
  firstName?: string;
  lastName?: string;
  email?: string;
  countryCode?: CountryCode;
  password?: string;
  confirmPassword?: string;
  code?: string;
  primaryLanguage?: string;
};

type Errors = {
  [key in keyof Account]?: string;
};

type Touched = {
  [key in keyof Account]?: boolean;
};

function useSignInForm(initialData: Account) {
  const [account, setAccount] = useState<Account>(initialData);
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Touched>({});
  const [mfaMode, setMfaMode] = useState<boolean>(false);
  const [passwordStatus, setPasswordStatus] = useState<PasswordStatus[]>([]);

  useEffect(() => {
    onValidate();
    // eslint-disable-next-line
  }, [account]);

  const onValidate = () => {
    const emailRegex =
      /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

    const newErrors: Errors = {};

    if (account.email && !emailRegex.test(account.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (initialData.email !== undefined && !account.email) {
      newErrors.email = "Email is required";
    }

    if (initialData.password !== undefined && !account.password) {
      newErrors.password = "Password is required";
    }

    if (initialData.firstName !== undefined && !account.firstName) {
      newErrors.firstName = "First name is required";
    }

    if (initialData.primaryLanguage !== undefined && !account.primaryLanguage) {
      newErrors.primaryLanguage = "Primary language is required";
    }

    if (
      initialData.countryCode !== undefined &&
      (!account.countryCode || !account.countryCode.code)
    ) {
      newErrors.countryCode = "Country is required";
    }

    if (mfaMode && !account.code) {
      newErrors.code = "Code is required";
    }

    if (initialData.confirmPassword !== undefined && account.password) {
      const lowercaseRegex = /[a-z]/;
      const uppercaseRegex = /[A-Z]/;
      const numberSymbolRegex = /[0-9\W]/;
      let status = [...passwordStatus];

      if (account.password.length < 8) {
        status = status.filter(item => item !== PasswordStatus.MinimumLength);
      } else {
        if (!status.includes(PasswordStatus.MinimumLength)) {
          status = [...status, PasswordStatus.MinimumLength];
        }
      }

      if (!lowercaseRegex.test(account.password)) {
        status = status.filter(item => item !== PasswordStatus.Lowercase);
      } else {
        if (!status.includes(PasswordStatus.Lowercase)) {
          status = [...status, PasswordStatus.Lowercase];
        }
      }

      if (!uppercaseRegex.test(account.password)) {
        status = status.filter(item => item !== PasswordStatus.Uppercase);
      } else {
        if (!status.includes(PasswordStatus.Uppercase)) {
          status = [...status, PasswordStatus.Uppercase];
        }
      }

      if (!numberSymbolRegex.test(account.password)) {
        status = status.filter(item => item !== PasswordStatus.NumberOrSymbol);
      } else {
        if (!status.includes(PasswordStatus.NumberOrSymbol)) {
          status = [...status, PasswordStatus.NumberOrSymbol];
        }
      }
      setPasswordStatus(status);
      if (status.length < 4) {
        newErrors.password = "Password is not valid";
      }
    }

    if (initialData.confirmPassword !== undefined && !account.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    }

    if (
      initialData.confirmPassword !== undefined &&
      account.confirmPassword !== account.password
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const onTouched = (key: string) => {
    setTouched({ ...touched, [key]: true });
  };

  return {
    touched,
    account,
    setAccount,
    onValidate,
    onTouched,
    setTouched,
    mfaMode,
    setMfaMode,
    errors,
    passwordStatus,
  };
}

export default useSignInForm;
